import './TQServiceRequestList.scss';

import {
  type MutableRefObject,
  useEffect,
  useRef,
} from 'react';

import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroller';

import {
  resetServiceRequestsFilters,
  setServiceRequestsFilters,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useTQConnectServiceRequests,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { ROUTES } from '../../../../types/routes';
// import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import {
  IServiceRequestsFilters,
  ITQServiceRequest,
  TQServiceRequestStatus,
  TQServiceRequestType,
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';
import { SERVICE_REQUEST_LIST_SCROLL } from '../../constants';
import TQServiceCard from '../TQServiceCard/TQServiceCard';
import TQServiceFilterSortOption
  from '../TQServiceFilterSortOption/TQServiceFilterSortOption';

interface TQServiceRequestListProps {
  requestType: TQServiceRequestTypeValue;
  /** Optional ref to the scroll container. Pass this when the list is inside a custom scroll area so infinite scroll attaches to the correct element. */
  scrollContainerRef?: MutableRefObject<HTMLDivElement | null>;
}

const TQServiceRequestList = ({
  requestType,
  scrollContainerRef: scrollContainerRefProp,
}: TQServiceRequestListProps) => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user);
  const { serviceRequestsFilters } = useAppSelector(
    (state) => state.tqConnectServiceRequest,
  );
  const scrollContainer = useRef<HTMLDivElement | null>(
    null,
  ) as MutableRefObject<HTMLDivElement | null>;

  const {
    data: serviceRequestsResult,
    isFetchingNextPage,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetching,
  } = useTQConnectServiceRequests(
    serviceRequestsFilters,
    serviceRequestsFilters?.type === requestType,
  );

  const onFiltersChange = <Key extends keyof IServiceRequestsFilters>(
    key: Key,
    value: IServiceRequestsFilters[Key],
  ) => {
    dispatch(setServiceRequestsFilters({ [key]: value }));
  };

  useEffect(() => {
    onFiltersChange('type', requestType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType]);

  useEffect(() => {
    if (scrollContainerRefProp) return;
    const element = document.getElementById(SERVICE_REQUEST_LIST_SCROLL);
    if (element) {
      scrollContainer.current = element as HTMLDivElement;
    }
  }, [scrollContainerRefProp]);

  useEffect(() => {
    dispatch(
      resetServiceRequestsFilters({
        type: requestType,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType]);

  const isMyActivity =
    requestType === TQServiceRequestType.REQUEST_RAISED ||
    requestType === TQServiceRequestType.REQUEST_RECEIVED ||
    requestType === TQServiceRequestType.RESPONDED;

  const getCreatorForRequest = (serviceRequest: ITQServiceRequest) => {
    if (!isMyActivity) {
      return {
        avatarUrl: serviceRequest.requestedBy?.profilePicUrl,
        name: serviceRequest.requestedBy?.name ?? '',
        actionLabel: 'Requested',
        timestamp: moment(serviceRequest.createdAt).format('MMM DD, YYYY'),
      };
    }
    const isCreatedByMe = currentUser?.id === serviceRequest.requestedBy?._id;
    if (requestType === TQServiceRequestType.REQUEST_RAISED && isCreatedByMe) {
      if (
        serviceRequest.visibility === 'private' &&
        serviceRequest.requestedTo?.name
      ) {
        return {
          avatarUrl: serviceRequest.requestedTo?.profilePicUrl,
          name: 'You',
          actionLabel: `requested this service from ${serviceRequest.requestedTo.name}`,
          timestamp: moment(serviceRequest.createdAt).format('MMM DD, YYYY'),
        };
      }
      return {
        avatarUrl: serviceRequest.requestedBy?.profilePicUrl,
        name: 'You',
        actionLabel: 'created this service request',
        timestamp: moment(serviceRequest.createdAt).format('MMM DD, YYYY'),
      };
    }
    return {
      avatarUrl: serviceRequest.requestedBy?.profilePicUrl,
      name: serviceRequest.requestedBy?.name ?? '',
      actionLabel: 'Requested',
      timestamp: moment(serviceRequest.createdAt).format('MMM DD, YYYY'),
    };
  };

  return (
    <div className="tq-service-request-list">
      <TQServiceFilterSortOption
        totalItems={serviceRequestsResult?.total}
        requestType={requestType}
      />
      <div className="tq-service-request-list__content">
        <InfiniteScroll
          pageStart={0}
          loadMore={() => {
            if (!isFetchingNextPage && !isFetching && hasMore) {
              fetchNextPage();
            }
          }}
          style={{ width: '100%' }}
          hasMore={hasMore && !isFetchingNextPage}
          threshold={250}
          loader={
            (isFetchingNextPage ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  paddingTop: 12,
                }}>
                <div className="tq-service-request-list__loader">
                  Loading more…
                </div>
              </div>
            ) : null) as JSX.Element
          }
          useWindow={false}
          getScrollParent={() =>
            (scrollContainerRefProp?.current ??
              scrollContainer.current) as HTMLElement
          }>
          {serviceRequestsResult?.data?.map((serviceRequest) => (
            <TQServiceCard
              key={serviceRequest._id}
              serviceRequest={serviceRequest}
              type={serviceRequest.visibility}
              title={serviceRequest.title}
              description={serviceRequest.description}
              tags={serviceRequest.tags.map((tag) => tag.name)}
              creator={getCreatorForRequest(serviceRequest)}
              onClick={() => {
                navigate(
                  ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DETAILS,
                  {
                    serviceRequestId: serviceRequest._id,
                  },
                  {
                    search: `from=${requestType}`,
                    reset: true,
                  },
                );
              }}
              status={serviceRequest.status as TQServiceRequestStatus}
              showOpenButton={false}
              helpOfferedCount={serviceRequest.totalResponses}
              requestType={requestType}
            />
          ))}
        </InfiniteScroll>
      </div>

      <div className="tq-service-request-list__no-data">
        {serviceRequestsResult?.data?.length ? (
          <p>That’s it! No more service requests to show.</p>
        ) : (
          <p>No service requests found</p>
        )}
      </div>
    </div>
  );
};

export default TQServiceRequestList;
